import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { InputStyled } from '../TextInput';
import { Button } from '../Button';
import { mergeTests } from '../../test-utilities/mergeTests';
import { isEqual } from 'lodash';

export interface ITagInputProps extends IFormCellProps {
  value?: string[] | null;
  onChange: (value?: string[] | null) => void;
  placeholder?: string;
  readOnly?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  regExp?: RegExp;
  splitBy?: string;
}

export const TagInput: React.FC<ITagInputProps> = (props) => {
  const { 'data-test-id': dataTestID } = props;
  const dataTestClass = props['data-test-class'] || 'tag-input';

  const [inputValue, setInputValue] = useState<string>('');
  const [arrayValue, setArrayValue] = useState<string[]>(props.value || []);

  const formCellProps = extractFormCellProps(props);

  useEffect(() => {
    const localValue = inputValue ? [...arrayValue, inputValue] : arrayValue;
    if (!isEqual(localValue, props.value)) {
      setArrayValue(props.value || []);
      setInputValue('');
    }
    // Disabling the line that wants to add arrayValue and inputValue to the dependencies. That's a behaviour change so
    //  not doing it as part of merging ui-toolkit.
    // eslint-disable-next-line
  }, [props.value]);

  const onFormUpdate = (values: string[]) => {
    const newVal = values.filter((v) => !!v);
    newVal.length === 0 ? props.onChange(null) : props.onChange(newVal);
  };

  const onInputChange = (value: string) => {
    const splitedValues = value
      .split(props.splitBy || ',')
      .map((v) => v.replace(props.regExp || new RegExp('[^0-9]', 'g'), ''));
    const filteredValues = splitedValues.filter((v) => !!v);
    if (splitedValues.length > 1) {
      setArrayValue([...arrayValue, ...filteredValues]);
      setInputValue('');
      onFormUpdate([...arrayValue, ...filteredValues]);
    } else {
      const val = filteredValues[0] ? [...arrayValue, filteredValues[0]] : arrayValue;
      setInputValue(splitedValues[0]);
      onFormUpdate(val);
    }
  };

  const onRemoveTag = (index: number) => {
    const newValue = arrayValue;
    newValue.splice(index, 1);
    setArrayValue(newValue);
    onFormUpdate([...newValue, inputValue]);
  };

  const onRemoveAll = () => {
    setArrayValue([]);
    setInputValue('');
    onFormUpdate([]);
  };

  //add on key down method: if e.key === enter then add it to the array and updated the form
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') {
      return;
    }
    const inputValue = (e.target as HTMLInputElement).value;
    if (inputValue) {
      const val = inputValue ? [...arrayValue, inputValue] : arrayValue;
      onFormUpdate(val);
      setInputValue('');
      setArrayValue(val);
    } else {
      onFormUpdate(arrayValue);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const val = [...arrayValue, e.target.value];
      onFormUpdate(val);
      setInputValue('');
      setArrayValue(val);
    } else {
      onFormUpdate(arrayValue);
    }
    props.onBlur && props.onBlur(e);
  };

  return (
    <FormCell {...formCellProps.formCellProps} data-test-id={dataTestID} data-test-class={dataTestClass}>
      <TagWrapper>
        <InputLine>
          <InputStyled
            value={inputValue}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            data-test-id={mergeTests(dataTestID, 'form-input')}
            data-test-class={mergeTests(dataTestID, 'input')}
            onBlur={onBlur}
            showErrorHighlight={!!props.errorText}
          />
          {arrayValue.length > 0 && (
            <Button intent={'ghost'} type={'button'} onClick={onRemoveAll}>
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          )}
        </InputLine>
        <TagLine>
          {arrayValue.map((tag, index) => (
            <TagStyled
              key={`${tag}-${index}`}
              data-test-class={mergeTests(dataTestClass, 'tag')}
              data-test-id={mergeTests(dataTestID, 'tag', tag)}
            >
              {tag}
              <FontAwesomeIcon icon={faTimes} onClick={() => onRemoveTag(index)} />
            </TagStyled>
          ))}
        </TagLine>
      </TagWrapper>
    </FormCell>
  );
};

const TagWrapper = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing4};
`;
const InputLine = styled(TagWrapper)`
  grid-template-columns: 1fr min-content;
`;

const TagLine = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.spacing4};
  flex-wrap: wrap;
`;

const TagStyled = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  padding: 0 ${(props) => props.theme.spacing.spacing4};
  gap: ${(props) => props.theme.spacing.spacing4};
  background-color: ${(props) => props.theme.colors.lightGray};
`;
